import { jsxs as _jsxs, Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import styled from "styled-components";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAppSelector } from "../../../../hooks";
import { selectMatchState } from "../../../../store/match";
import { ETeam, TeamName, TMatchResultCase, } from "../../../../types";
import { Delimiter } from "../../../atoms/Delimiter";
import { DataContainerNew } from "../../../atoms/DataContainer";
import { UnknownSourcePlayersTable } from "../../../molecules/UnknownSourcePlayers";
import { colors } from "../../../../styles/colors";
const PageContainer = styled(DataContainerNew) `
  flex-direction: column;
  align-items: center;
`;
const BackButton = styled.button `
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid ${colors.cardBorder};
  color: ${colors.textSecondary};
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${colors.primary};
    color: ${colors.textPrimary};
  }

  svg {
    font-size: 18px;
  }
`;
const ContentWrapper = styled.div `
  width: 100%;
  max-width: 700px;
`;
const WinnerBanner = styled.div `
  background: linear-gradient(135deg, ${colors.surface} 0%, ${colors.surfaceVariant} 100%);
  border: 1px solid ${colors.cardBorder};
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  margin-bottom: 24px;
`;
const EventDate = styled.div `
  color: ${colors.textSecondary};
  font-size: 14px;
  margin-bottom: 12px;
`;
const WinnerTitle = styled.h1 `
  font-family: "Bowler", sans-serif;
  font-size: 32px;
  color: ${colors.textPrimary};
  margin: 0;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;
const TeamSection = styled.div `
  background-color: ${colors.surface};
  border: 1px solid ${colors.cardBorder};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
`;
const TeamHeader = styled.h3 `
  color: ${colors.textPrimary};
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    color: ${colors.textSecondary};
    font-weight: 400;
  }
`;
const PlayerRow = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: ${colors.surfaceVariant};
  border-radius: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const PlayerName = styled.div `
  color: ${colors.textPrimary};
  font-weight: 500;

  span {
    color: ${colors.textSecondary};
    font-weight: 400;
  }
`;
const PlayerInfo = styled.div `
  display: flex;
  align-items: center;
  gap: 16px;
`;
const PlayerStatus = styled.span `
  color: ${props => props.alive ? colors.success : colors.error};
  font-size: 13px;
`;
const PlayerRole = styled.span `
  color: ${colors.textSecondary};
  font-size: 13px;
`;
const PlayerRating = styled.span `
  color: ${colors.accent};
  font-size: 13px;
  font-weight: 500;
`;
const getWinnerString = (result) => {
    switch (result) {
        case TMatchResultCase.MAFIA_WIN:
            return "Мафия победила";
        case TMatchResultCase.CIVILIANS_WIN:
            return "Мирные победили";
        case TMatchResultCase.FOOLS_WIN:
            return "Дурак обвел всех вокруг пальца";
        case TMatchResultCase.ANARCHIST_WIN:
            return "Победа анархиста";
        case TMatchResultCase.WEREWOLF_WIN:
            return "Победа оборотня";
        default:
            return "Ничья";
    }
};
const getTeamsList = (winCase) => {
    // eslint-disable-next-line default-case
    switch (winCase) {
        case TMatchResultCase.FOOLS_WIN:
            return [ETeam.FOOLS, ETeam.MAFIA, ETeam.CIVILIANS];
        case TMatchResultCase.CIVILIANS_WIN:
            return [ETeam.CIVILIANS, ETeam.MAFIA, ETeam.FOOLS];
        case TMatchResultCase.MAFIA_WIN:
            return [ETeam.MAFIA, ETeam.FOOLS, ETeam.CIVILIANS];
    }
    return [ETeam.CIVILIANS, ETeam.MAFIA, ETeam.FOOLS];
};
export function FinalTable() {
    var _a, _b, _c;
    const matchState = useAppSelector(selectMatchState);
    const nav = useNavigate();
    const onClickBack = () => {
        if (matchState === null) {
            return;
        }
        nav(`/matches?eventId=${matchState.eventId}`);
    };
    const allGamePlayers = useMemo(() => ((matchState === null || matchState === void 0 ? void 0 : matchState.state.players) || []), [matchState]);
    const playersInTeam = (team) => allGamePlayers === null || allGamePlayers === void 0 ? void 0 : allGamePlayers.filter((p) => p.team === team);
    const event = ((_a = matchState === null || matchState === void 0 ? void 0 : matchState.state) === null || _a === void 0 ? void 0 : _a.event) || null;
    const winCase = (_b = matchState === null || matchState === void 0 ? void 0 : matchState.state) === null || _b === void 0 ? void 0 : _b.winCase;
    const teams = getTeamsList(winCase);
    const renderPlayerRow = (player) => (_jsxs(PlayerRow, { children: [_jsx(PlayerName, { children: player.player.gameName !== null ? (_jsxs(_Fragment, { children: [player.player.gameName, " ", _jsxs("span", { children: ["(", player.player.realName, ")"] })] })) : (player.player.realName) }), _jsxs(PlayerInfo, { children: [_jsx(PlayerStatus, Object.assign({ alive: (player === null || player === void 0 ? void 0 : player.isAlive) || false }, { children: (player === null || player === void 0 ? void 0 : player.isAlive) ? "Выжил" : "Убит" })), _jsx(PlayerRole, { children: player.roleName }), _jsxs(PlayerRating, { children: ["+", player.rating] })] })] }, player.id));
    const renderTeamSection = (team) => {
        const players = playersInTeam(team);
        if (players.length === 0)
            return null;
        return (_jsxs(TeamSection, { children: [_jsxs(TeamHeader, { children: [TeamName(team), " ", _jsxs("span", { children: ["(", players.length, ")"] })] }), players.map(renderPlayerRow)] }, team));
    };
    return (_jsx(PageContainer, { children: _jsxs(ContentWrapper, { children: [_jsxs(BackButton, Object.assign({ onClick: onClickBack }, { children: [_jsx(ArrowBackIcon, {}), "\u041A \u0430\u0444\u0438\u0448\u0435"] })), _jsx(Delimiter, { top: "24px" }), _jsxs(WinnerBanner, { children: [(event === null || event === void 0 ? void 0 : event.date) && _jsx(EventDate, { children: event.date }), _jsx(WinnerTitle, { children: getWinnerString(((_c = matchState === null || matchState === void 0 ? void 0 : matchState.state) === null || _c === void 0 ? void 0 : _c.winCase) || "") })] }), teams.map(renderTeamSection), (matchState === null || matchState === void 0 ? void 0 : matchState.eventId) && (_jsxs(_Fragment, { children: [_jsx(Delimiter, { top: "16px" }), _jsx(UnknownSourcePlayersTable, { eventId: matchState.eventId, showTitle: true, compact: true })] }))] }) }));
}
